import {
  arrow,
  autoUpdate,
  FloatingArrow,
  FloatingPortal,
  offset,
  shift,
  useFloating,
  useHover,
  useInteractions,
  type Placement,
} from '@floating-ui/react'
import { clsx } from 'clsx'
import {
  type ComponentPropsWithoutRef,
  type ReactElement,
  type ReactNode,
  useRef,
  useState,
} from 'react'

import styles from './tooltip.module.scss'

export interface TooltipProps extends Omit<ComponentPropsWithoutRef<'div'>, 'content'> {
  /**
   * Content to display in the tooltip
   */
  content: ReactNode
  /**
   * The element that triggers the tooltip on hover
   */
  children: ReactElement
  /**
   * Delay in milliseconds before showing the tooltip
   */
  delay?: number
  /**
   * Whether the tooltip is disabled
   */
  disabled?: boolean
  /**
   * Additional className for the tooltip content
   */
  tooltipClassName?: string
  /**
   * Maximum width of the tooltip. Number means pixels, string allows notation with units, e.g. `calc(100 / var(--rem) * 1rem)` or `200px`
   */
  maxWidth?: number | string
  /**
   * Placement of the tooltip relative to the trigger
   */
  placement?: Placement
}

export function Tooltip(props: TooltipProps): ReactElement {
  const {
    content,
    children,
    delay = 200,
    disabled = false,
    className,
    tooltipClassName,
    maxWidth = 'calc(200 / var(--rem) * 1rem)',
    placement = 'right',
    ...rest
  } = props

  const [isOpen, setIsOpen] = useState(false)
  const arrowRef = useRef(null)

  const { refs, floatingStyles, context } = useFloating({
    open: isOpen,
    onOpenChange: setIsOpen,
    placement,
    middleware: [
      offset(10),
      shift({ padding: 8 }),
      arrow({
        element: arrowRef,
        padding: 6,
      }),
    ],
    whileElementsMounted: autoUpdate,
  })

  const hover = useHover(context, {
    delay: { open: delay, close: 0 },
    enabled: !disabled,
  })

  const { getReferenceProps, getFloatingProps } = useInteractions([hover])

  return (
    <>
      <div
        ref={refs.setReference}
        className={clsx(styles.trigger, className)}
        {...getReferenceProps(rest)}
      >
        {children}
      </div>
      {isOpen && !disabled && (
        <FloatingPortal>
          <div
            ref={refs.setFloating}
            style={floatingStyles}
            className={clsx(styles.tooltip, styles.visible, tooltipClassName)}
            {...getFloatingProps()}
          >
            <div
              className={styles.content}
              style={{
                maxWidth: typeof maxWidth === 'number' ? `${maxWidth}px` : maxWidth,
              }}
            >
              {content}
            </div>
            <FloatingArrow ref={arrowRef} context={context} className={styles.arrow} />
          </div>
        </FloatingPortal>
      )}
    </>
  )
}
